'use client'

import { useReveal } from '@/lib/hooks/use-reveal'
import { useState, useEffect, useRef, MutableRefObject } from 'react'
import {
  Education as GraduationCap,
  Purchase as CreditCard,
  Group as Users,
  Bullhorn as Megaphone,
  Settings,
  Grid as LayoutGrid,
  ArrowRight,
  Home,
  ChevronRight,
} from '@carbon/icons-react'
import { FEATURES, CATEGORIES } from '@content/schoolerp/features'
import Link from 'next/link'
import { AnimatedNumber } from '@/components/ui/animated-number'
import { Button } from '@/components/ui/button'
// ─── TYPES ───
interface HeroStat {
  value: string
  label: string
}
interface Feature {
  slug: string
  categorySlug: string
  category: string
  title: string
  tagline: string
  heroStat: HeroStat[]
}
interface Category {
  slug: string
  icon: React.ReactNode
  label: string
}

// ─── ANIMATION HOOK ───
/* Shared fail-safe reveal — see `@/lib/hooks/use-reveal`. The ratio-gated
   local copy this replaces stranded sections at opacity 0 when a sample was
   missed. */
function useInView(
  threshold = 0.1
): [MutableRefObject<HTMLElement | null>, boolean] {
  const { ref, shown } = useReveal<HTMLElement>(threshold)
  return [ref as MutableRefObject<HTMLElement | null>, shown]
}
const CATEGORY_ICON_MAP: Record<string, React.ReactNode> = {
  academic: <GraduationCap size={13} />,
  finance: <CreditCard size={13} />,
  staff: <Users size={13} />,
  communication: <Megaphone size={13} />,
  admin: <Settings size={13} />,
}
// ─── SCHOOL ILLUSTRATION ───
function SchoolIllustration() {
  return (
    <svg
      viewBox="0 0 520 400"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="w-full h-full"
    >
      <rect width="520" height="400" rx="20" fill="#131314" />
      {/* Stars */}
      {[
        [60, 40],
        [130, 22],
        [210, 38],
        [310, 18],
        [400, 45],
        [480, 28],
        [80, 70],
        [170, 55],
        [350, 30],
        [450, 65],
      ].map(([x, y], i) => (
        <circle
          key={i}
          cx={x}
          cy={y}
          r={i % 2 === 0 ? 1.5 : 1}
          fill="white"
          opacity={0.15 + Math.random() * 0.15}
        />
      ))}
      {/* Moon */}
      <circle cx="440" cy="48" r="20" fill="#1c1c1e" />
      <circle cx="451" cy="42" r="16" fill="#131314" />
      {/* Ground */}
      <rect x="20" y="348" width="480" height="52" rx="4" fill="#1c1c1e" />
      {/* Main building */}
      <rect
        x="108"
        y="155"
        width="304"
        height="200"
        rx="4"
        fill="#1f1f23"
        stroke="#35353a"
        strokeWidth="1.5"
      />
      {/* Roof */}
      <polygon
        points="88,155 260,78 432,155"
        fill="#18181a"
        stroke="#35353a"
        strokeWidth="1.5"
      />
      {/* Flagpole */}
      <rect x="257" y="66" width="3" height="32" fill="#35353a" />
      <polygon points="260,68 278,76 260,84" fill="#61ffcd" opacity="0.7" />
      {/* School sign */}
      <rect
        x="173"
        y="170"
        width="174"
        height="24"
        rx="4"
        fill="#131314"
        stroke="#35353a"
        strokeWidth="1"
      />
      <text
        x="260"
        y="187"
        textAnchor="middle"
        fill="#61ffcd"
        fontSize="10"
        fontFamily="monospace"
        fontWeight="700"
        letterSpacing="1"
      >
        EUCLIDEUM ERP
      </text>
      {/* Main door */}
      <rect
        x="220"
        y="292"
        width="80"
        height="62"
        rx="3"
        fill="#131314"
        stroke="#35353a"
        strokeWidth="1.5"
      />
      <rect
        x="220"
        y="292"
        width="40"
        height="62"
        fill="#1c1c1e"
        opacity="0.5"
      />
      <circle cx="256" cy="324" r="3" fill="#61ffcd" opacity="0.4" />
      <circle cx="264" cy="324" r="3" fill="#61ffcd" opacity="0.4" />
      {/* Windows row 1 */}
      {[138, 222, 306, 375].map((x, i) => (
        <g key={i}>
          <rect
            x={x}
            y="205"
            width="46"
            height="40"
            rx="3"
            fill="#131314"
            stroke="#35353a"
            strokeWidth="1"
          />
          <rect
            x={x + 2}
            y="207"
            width="42"
            height="18"
            rx="2"
            fill="#61ffcd"
            opacity={[0.07, 0.09, 0.06, 0.05][i]}
          />
          <line
            x1={x + 23}
            y1="207"
            x2={x + 23}
            y2="243"
            stroke="#35353a"
            strokeWidth="0.8"
          />
        </g>
      ))}
      {/* Windows row 2 */}
      {[152, 370].map((x, i) => (
        <g key={i}>
          <rect
            x={x}
            y="262"
            width="44"
            height="36"
            rx="3"
            fill="#131314"
            stroke="#35353a"
            strokeWidth="1"
          />
          <rect
            x={x + 2}
            y="264"
            width="40"
            height="16"
            rx="2"
            fill="#61ffcd"
            opacity={[0.06, 0.04][i]}
          />
          <line
            x1={x + 22}
            y1="264"
            x2={x + 22}
            y2="296"
            stroke="#35353a"
            strokeWidth="0.8"
          />
        </g>
      ))}
      {/* Left wing */}
      <rect
        x="28"
        y="215"
        width="88"
        height="142"
        rx="3"
        fill="#18181a"
        stroke="#35353a"
        strokeWidth="1"
      />
      <rect
        x="42"
        y="235"
        width="34"
        height="28"
        rx="2"
        fill="#131314"
        stroke="#35353a"
        strokeWidth="0.8"
      />
      <rect
        x="44"
        y="237"
        width="30"
        height="12"
        rx="1"
        fill="#61ffcd"
        opacity="0.06"
      />
      <rect
        x="42"
        y="278"
        width="34"
        height="28"
        rx="2"
        fill="#131314"
        stroke="#35353a"
        strokeWidth="0.8"
      />
      <rect
        x="44"
        y="280"
        width="30"
        height="12"
        rx="1"
        fill="#61ffcd"
        opacity="0.04"
      />
      {/* Right wing */}
      <rect
        x="404"
        y="215"
        width="88"
        height="142"
        rx="3"
        fill="#18181a"
        stroke="#35353a"
        strokeWidth="1"
      />
      <rect
        x="438"
        y="235"
        width="34"
        height="28"
        rx="2"
        fill="#131314"
        stroke="#35353a"
        strokeWidth="0.8"
      />
      <rect
        x="440"
        y="237"
        width="30"
        height="12"
        rx="1"
        fill="#61ffcd"
        opacity="0.06"
      />
      <rect
        x="438"
        y="278"
        width="34"
        height="28"
        rx="2"
        fill="#131314"
        stroke="#35353a"
        strokeWidth="0.8"
      />
      <rect
        x="440"
        y="280"
        width="30"
        height="12"
        rx="1"
        fill="#61ffcd"
        opacity="0.04"
      />
      {/* Path */}
      <path
        d="M178 358 L260 348 L342 358"
        stroke="#35353a"
        strokeWidth="8"
        strokeLinecap="round"
        opacity="0.5"
      />
      {/* Stat card: Attendance */}
      <g transform="translate(6,122)">
        <rect
          width="126"
          height="48"
          rx="8"
          fill="#1f1f23"
          stroke="#35353a"
          strokeWidth="1"
        />
        <circle cx="17" cy="16" r="7" fill="rgba(97,255,205,0.08)" />
        <text x="17" y="20" textAnchor="middle" fill="#61ffcd" fontSize="9">
          ✓
        </text>
        <text
          x="31"
          y="12"
          fill="rgba(255,255,255,0.3)"
          fontSize="7.5"
          fontFamily="monospace"
        >
          ATTENDANCE
        </text>
        <text
          x="31"
          y="24"
          fill="white"
          fontSize="11"
          fontFamily="monospace"
          fontWeight="700"
        >
          98.4%
        </text>
        <rect x="10" y="34" width="76" height="3" rx="2" fill="#35353a" />
        <rect
          x="10"
          y="34"
          width="66"
          height="3"
          rx="2"
          fill="#61ffcd"
          opacity="0.4"
        />
      </g>
      {/* Stat card: Fees */}
      <g transform="translate(388,108)">
        <rect
          width="124"
          height="48"
          rx="8"
          fill="#1f1f23"
          stroke="#35353a"
          strokeWidth="1"
        />
        <circle cx="17" cy="16" r="7" fill="rgba(97,255,205,0.08)" />
        <text x="17" y="20" textAnchor="middle" fill="#61ffcd" fontSize="9">
          ₹
        </text>
        <text
          x="31"
          y="12"
          fill="rgba(255,255,255,0.3)"
          fontSize="7.5"
          fontFamily="monospace"
        >
          FEES
        </text>
        <text
          x="31"
          y="24"
          fill="white"
          fontSize="11"
          fontFamily="monospace"
          fontWeight="700"
        >
          ₹24.6L
        </text>
        <rect x="10" y="34" width="76" height="3" rx="2" fill="#35353a" />
        <rect
          x="10"
          y="34"
          width="70"
          height="3"
          rx="2"
          fill="#61ffcd"
          opacity="0.4"
        />
      </g>
      {/* Stat card: Students */}
      <g transform="translate(388,168)">
        <rect
          width="124"
          height="40"
          rx="8"
          fill="#1f1f23"
          stroke="#35353a"
          strokeWidth="1"
        />
        <circle cx="17" cy="14" r="7" fill="rgba(97,255,205,0.08)" />
        <text x="17" y="18" textAnchor="middle" fill="#61ffcd" fontSize="9">
          👥
        </text>
        <text
          x="31"
          y="10"
          fill="rgba(255,255,255,0.3)"
          fontSize="7.5"
          fontFamily="monospace"
        >
          STUDENTS
        </text>
        <text
          x="31"
          y="22"
          fill="white"
          fontSize="11"
          fontFamily="monospace"
          fontWeight="700"
        >
          1,240
        </text>
        <text
          x="10"
          y="34"
          fill="rgba(255,255,255,0.22)"
          fontSize="7.5"
          fontFamily="monospace"
        >
          ↑ 34 new this month
        </text>
      </g>
    </svg>
  )
}

// ─── ANIMATED WORD ───
function AnimatedWord({ words }: { words: string[] }) {
  const [idx, setIdx] = useState(0)
  const [visible, setVisible] = useState(true)
  useEffect(() => {
    const t = setInterval(() => {
      setVisible(false)
      setTimeout(() => {
        setIdx((i) => (i + 1) % words.length)
        setVisible(true)
      }, 380)
    }, 2200)
    return () => clearInterval(t)
  }, [words])
  return (
    <span
      className="inline-block text-emerald-600 dark:text-primary-active border-b-2 border-emerald-600/40 dark:border-primary-active/40 pb-0.5 min-w-[140px] sm:min-w-[160px] md:min-w-[200px] transition-all duration-350 ease-out"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(-8px)',
      }}
    >
      {words[idx]}
    </span>
  )
}

// ─── STAT PILL ───
function StatPill({ value, label }: { value: string; label: string }) {
  const [ref, inView] = useInView(0.2)
  return (
    <div
      ref={ref as MutableRefObject<HTMLDivElement>}
      className="flex flex-col items-center transition-all duration-600"
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? 'translateY(0)' : 'translateY(16px)',
      }}
    >
      <div className="font-['Lora',Georgia,serif] text-[clamp(20px,5vw,26px)] font-bold text-emerald-600 dark:text-primary-active leading-none mb-1">
        <AnimatedNumber value={value} />
      </div>
      <div className="text-[10px] sm:text-[11px] text-muted-foreground text-center max-w-[60px] sm:max-w-[80px] leading-relaxed">
        {label}
      </div>
    </div>
  )
}

// ─── FEATURE CARD ───
function FeatureCard({ feature, delay }: { feature: Feature; delay: number }) {
  const [ref, inView] = useInView()
  const [hov, setHov] = useState(false)

  return (
    <Link
      ref={ref as MutableRefObject<HTMLAnchorElement>}
      href={`/schoolerp/features/${feature.slug}`}
      className="flex flex-col no-underline rounded-xl relative overflow-hidden transition-all duration-450 cursor-pointer"
      style={{
        background: hov
          ? 'hsl(var(--primary-active) / 0.04)'
          : 'hsl(var(--foreground) / 0.02)',
        border: `1px solid ${hov ? 'hsl(var(--primary-active) / 0.18)' : 'hsl(var(--border) / 0.5)'}`,
        padding: 'clamp(14px, 4vw, 18px) clamp(12px, 3vw, 16px)',
        transform: inView
          ? `translateY(${hov ? -2 : 0}px)`
          : 'translateY(22px)',
        opacity: inView ? 1 : 0,
        transitionDelay: `${delay}ms`,
      }}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
    >
      {/* Teal accent top line */}
      <div
        className="absolute top-0 left-0 right-0 h-0.5 bg-emerald-600 dark:bg-primary-active rounded-t-xl transition-transform duration-250 origin-left"
        style={{ transform: hov ? 'scaleX(1)' : 'scaleX(0)' }}
      />

      {/* Category badge */}
      {/* Category badge */}
      <div className="inline-flex items-center gap-1.5 bg-emerald-600/5 dark:bg-primary-active/5 border border-emerald-600/10 dark:border-primary-active/10 rounded-md px-2 py-1 mb-2.5 self-start text-[9px] sm:text-[10px] font-bold tracking-[0.1em] uppercase text-muted-foreground">
        <span className="text-emerald-600/70 dark:text-primary-active/70 flex">
          {CATEGORY_ICON_MAP[feature.categorySlug]}
        </span>
        {feature.category}
      </div>

      {/* Title */}
      <div className="font-['Lora',Georgia,serif] text-[clamp(13px,4vw,15px)] text-foreground mb-1.5 leading-tight font-semibold">
        {feature.title}
      </div>

      {/* Tagline */}
      <div className="text-[11px] sm:text-[12px] text-muted-foreground leading-relaxed mb-3 flex-1">
        {feature.tagline}
      </div>

      {/* Stats */}
      <div className="flex gap-1.5 flex-wrap mb-3">
        {feature.heroStat.slice(0, 2).map((s) => (
          <span
            key={s.label}
            className="bg-foreground/5 border border-border/50 text-muted-foreground text-[9px] sm:text-[10px] font-semibold px-1.5 sm:px-2 py-0.5 rounded font-mono"
          >
            {s.value} {s.label}
          </span>
        ))}
      </div>

      {/* Explore link */}
      <div
        className="flex items-center gap-1 text-[10px] sm:text-[11px] font-bold text-emerald-600 dark:text-primary-active tracking-[0.04em] transition-opacity duration-200"
        style={{ opacity: hov ? 1 : 0.35 }}
      >
        Explore
        <ArrowRight
          size={10}
          className="transition-transform duration-200"
          style={{ transform: hov ? 'translateX(3px)' : 'translateX(0)' }}
        />
      </div>
    </Link>
  )
}

// ─── CATEGORY ICON WRAPPER ───

// ─── MAIN PAGE ───
export default function FeaturesIndex() {
  const [activeCategory, setActiveCategory] = useState<string>('all')
  const [animKey, setAnimKey] = useState(0)
  const [heroIn, setHeroIn] = useState(false)

  useEffect(() => {
    setTimeout(() => setHeroIn(true), 100)
  }, [])

  const filtered =
    activeCategory === 'all'
      ? FEATURES
      : FEATURES.filter((f) => f.categorySlug === activeCategory)

  const handleCat = (slug: string) => {
    setActiveCategory(slug)
    setAnimKey((k) => k + 1)
  }

  return (
    <div className="bg-background text-foreground font-['DM_Sans',system-ui,sans-serif] min-h-screen overflow-x-hidden">
      {/* ══════════════════════════════════════════
          HERO SECTION
      ══════════════════════════════════════════ */}
      <section className="relative overflow-hidden bg-background border-b border-border min-h-[92vh] flex items-center">
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 md:px-8 lg:px-12 py-12 sm:py-16 md:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 lg:gap-16 items-center">
            {/* LEFT Text content */}
            <div className="relative z-2">
              {/* Breadcrumb */}
              <div
                className={`flex items-center gap-1.5 mb-6 sm:mb-7 text-[10px] sm:text-[11px] text-muted-foreground transition-all duration-500`}
                style={{
                  opacity: heroIn ? 1 : 0,
                  transform: heroIn ? 'translateY(0)' : 'translateY(12px)',
                }}
              >
                <Home size={11} />
                <Link href="/" className="text-inherit no-underline">
                  Home
                </Link>
                <ChevronRight size={10} />
                {/* The trail skipped its own parent, so this index was a dead
                    end back toward the School ERP page. */}
                <Link href="/schoolerp" className="text-inherit no-underline">
                  School ERP
                </Link>
                <ChevronRight size={10} />
                <span className="text-emerald-600 dark:text-primary-active">
                  Features
                </span>
              </div>

              {/* Eyebrow pill */}
              <div
                className={`inline-flex items-center gap-2 bg-emerald-600/10 dark:bg-primary-active/10 rounded-full backdrop-blur-md px-3 py-1.5 mb-5 sm:mb-6 transition-all duration-550`}
                style={{
                  opacity: heroIn ? 1 : 0,
                  transform: heroIn ? 'translateY(0)' : 'translateY(16px)',
                  transitionDelay: '0.05s',
                }}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-600 dark:bg-primary-active inline-block animate-pulse" />
                <span className="text-[9px] sm:text-[10px] font-bold tracking-[0.14em] uppercase text-emerald-600 dark:text-primary-active">
                  Platform capabilities
                </span>
              </div>

              {/* Headline */}
              {/* <h1 className={`font-['Lora',Georgia,serif] text-[clamp(28px,7vw,48px)] font-bold text-white leading-[1.12] mb-1 sm:mb-2 tracking-[-0.01em] transition-all duration-650`}
                style={{
                  opacity: heroIn ? 1 : 0,
                  transform: heroIn ? "translateY(0)" : "translateY(22px)",
                  transitionDelay: "0.1s",
                }}>
                Built for schools that run on
              </h1>
              <h1 className={`font-['Lora',Georgia,serif] text-[clamp(28px,7vw,48px)] font-bold leading-[1.12] mb-4 sm:mb-5 tracking-[-0.01em] transition-all duration-650`}
                style={{
                  opacity: heroIn ? 1 : 0,
                  transform: heroIn ? "translateY(0)" : "translateY(22px)",
                  transitionDelay: "0.15s",
                }}>
                <AnimatedWord words={["ambition.", "excellence.", "discipline.", "results.", "growth."]} />
              </h1> */}
              <h1
                className="font-['Lora',Georgia,serif] text-[clamp(28px,6vw,48px)] font-bold text-foreground leading-[1.12] tracking-[-0.01em]"
                style={{
                  opacity: heroIn ? 1 : 0,
                  transform: heroIn ? 'translateY(0)' : 'translateY(22px)',
                  transitionDelay: '0.1s',
                }}
              >
                Built for schools that
                <br />
                <span className="whitespace-nowrap">
                  run on{' '}
                  <AnimatedWord
                    words={[
                      'Ambition.',
                      'Excellence.',
                      'Discipline.',
                      'Results.',
                      'Growth.',
                    ]}
                  />
                </span>
              </h1>
              {/* Sub */}
              <p
                className={`text-[13px] sm:text-[14px] md:text-[15px] text-muted-foreground leading-relaxed sm:leading-[1.8] max-w-[460px] mb-8 sm:mb-9 transition-all duration-650`}
                style={{
                  opacity: heroIn ? 1 : 0,
                  transform: heroIn ? 'translateY(0)' : 'translateY(18px)',
                  transitionDelay: '0.2s',
                }}
              >
                {FEATURES.length}+ deeply connected modules covering every
                school operation from admissions to alumni. One platform, zero
                data gaps.
              </p>

              {/* CTA Row */}
              <div
                className={`flex flex-col sm:flex-row gap-3 sm:gap-4 mb-10 sm:mb-11 transition-all duration-650`}
                style={{
                  opacity: heroIn ? 1 : 0,
                  transform: heroIn ? 'translateY(0)' : 'translateY(16px)',
                  transitionDelay: '0.25s',
                }}
              >
                <Button asChild size="xl">
                  <Link href="#features">Explore all modules</Link>
                </Button>
                <Button asChild variant="outline" size="xl">
                  <Link href="/schedule-meeting">Request Demo</Link>
                </Button>
              </div>

              {/* Stats row */}
              <div
                className={`flex flex-wrap gap-4 sm:gap-6 md:gap-7 pt-6 sm:pt-7 border-t border-border transition-all duration-650`}
                style={{
                  opacity: heroIn ? 1 : 0,
                  transform: heroIn ? 'translateY(0)' : 'translateY(14px)',
                  transitionDelay: '0.32s',
                }}
              >
                <StatPill
                  value={`${FEATURES.length}+`}
                  label="Platform modules"
                />
                <div className="w-px bg-border hidden sm:block" />
                <StatPill value="50+" label="Schools live" />
                <div className="w-px bg-border hidden sm:block" />
                <StatPill value="95%" label="Fee collection rate" />
                <div className="w-px bg-border hidden sm:block" />
                <StatPill value="2 to 4w" label="Go-live timeline" />
              </div>
            </div>

            {/* RIGHT Illustration */}
            <div
              className={`relative flex items-center justify-center transition-all duration-900 mt-8 lg:mt-0`}
              style={{
                opacity: heroIn ? 1 : 0,
                transform: heroIn
                  ? 'translateY(0) scale(1)'
                  : 'translateY(30px) scale(0.97)',
                transitionDelay: '0.1s',
              }}
            >
              <div className="relative w-full max-w-[380px] sm:max-w-[420px] md:max-w-[480px]">
                {/* Rotating rings */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] sm:w-[380px] md:w-[460px] h-[300px] sm:h-[380px] md:h-[460px] border border-dashed border-emerald-600/10 dark:border-primary-active/10 rounded-full animate-[spin_70s_linear_infinite] pointer-events-none" />
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[220px] sm:w-[280px] md:w-[340px] h-[220px] sm:h-[280px] md:h-[340px] border border-dashed border-emerald-600/5 dark:border-primary-active/5 rounded-full animate-[spin_45s_linear_infinite_reverse] pointer-events-none" />

                <div className="animate-[floatA_5s_ease-in-out_infinite]">
                  <SchoolIllustration />
                </div>

                {/* Floating badge top-right */}
                <div className="absolute top-[5%] right-[-2%] sm:top-[8%] sm:right-[-4%] bg-card/95 border border-emerald-600/20 dark:border-primary-active/20 rounded-xl p-2 sm:p-2.5 flex items-center gap-2 sm:gap-2.5 shadow-2xl animate-[floatB_6.5s_ease-in-out_infinite]">
                  <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-lg bg-emerald-600/10 dark:bg-primary-active/10 flex items-center justify-center text-xs sm:text-sm flex-shrink-0">
                    📊
                  </div>
                  <div>
                    <div className="text-[7px] sm:text-[8px] md:text-[9px] text-muted-foreground font-mono tracking-[0.1em] uppercase">
                      Result published
                    </div>
                    <div className="text-[10px] sm:text-[11px] md:text-[12px] font-semibold text-foreground">
                      Class 10 All sections
                    </div>
                  </div>
                </div>

                {/* Floating badge bottom-left */}
                <div className="absolute bottom-[8%] left-[-4%] sm:bottom-[12%] sm:left-[-6%] bg-card/95 border border-emerald-600/10 dark:border-primary-active/10 rounded-xl p-2 sm:p-2.5 flex items-center gap-2 sm:gap-2.5 shadow-2xl animate-[floatA_5s_ease-in-out_infinite]">
                  <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-lg bg-emerald-600/10 dark:bg-primary-active/10 flex items-center justify-center text-xs sm:text-sm flex-shrink-0">
                    💰
                  </div>
                  <div>
                    <div className="text-[7px] sm:text-[8px] md:text-[9px] text-muted-foreground font-mono tracking-[0.1em] uppercase">
                      Fee reminder
                    </div>
                    <div className="text-[10px] sm:text-[11px] md:text-[12px] font-semibold text-foreground">
                      240 parents notified
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════
          FEATURES SECTION
      ══════════════════════════════════════════ */}
      <section id="features" className="bg-background border-t border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 md:px-8 py-12 sm:py-16 md:py-20">
          {/* Section header */}
          <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-4 mb-6 sm:mb-7">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-4 sm:w-5 h-0.5 bg-emerald-600 dark:bg-primary-active rounded-full" />
                <span className="text-[9px] sm:text-[10px] font-bold tracking-[0.16em] uppercase text-emerald-600 dark:text-primary-active">
                  All modules
                </span>
              </div>
              <h2 className="font-['Lora',Georgia,serif] text-[clamp(20px,5vw,32px)] font-bold text-foreground leading-tight">
                Every feature, in one place
              </h2>
            </div>
            <div className="text-[11px] sm:text-[12px] text-muted-foreground font-mono">
              {FEATURES.length} modules · 5 categories
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {FEATURES.map((f, i) => (
              <FeatureCard key={f.slug} feature={f} delay={i * 35} />
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════
          BOTTOM CTA
      ══════════════════════════════════════════ */}
      <section className="bg-card border-t border-border py-12 sm:py-16 md:py-20 text-center relative overflow-hidden">
        <div className="relative max-w-[400px] sm:max-w-[450px] md:max-w-[500px] mx-auto px-4">
          <div className="font-['Lora',Georgia,serif] text-[clamp(20px,5vw,36px)] font-bold text-foreground mb-2 sm:mb-3 leading-tight">
            Ready to see it all live?
          </div>
          <p className="text-[13px] sm:text-[14px] md:text-[15px] text-muted-foreground mb-6 sm:mb-7 leading-relaxed">
            Book a 30-minute demo we walk through every module that matters to
            your school.
          </p>
          <Button asChild size="xl">
            <Link href="/schoolerp#demo">Book a free demo</Link>
          </Button>
        </div>
      </section>

      {/* Add keyframe animations */}
      <style jsx global>{`
        @keyframes floatA {
          0%,
          100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-10px);
          }
        }
        @keyframes floatB {
          0%,
          100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(8px);
          }
        }
        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
        @keyframes pulse {
          0%,
          100% {
            opacity: 1;
            transform: scale(1);
          }
          50% {
            opacity: 0.4;
            transform: scale(1.25);
          }
        }
        .animate-pulse {
          animation: pulse 2.5s ease-in-out infinite;
        }
      `}</style>
    </div>
  )
}
